import React, { useRef } from 'react';

import { InputAdornment, TextField } from '@mui/material';
import { withStyles } from '@mui/styles';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import uuid from 'uuid';

import SearchTextFieldItem from 'components/SearchTextField/SearchTextFieldItem';
import Clear from 'components/SvgComponents/icons/Clear';
import Search from 'components/SvgComponents/icons/Search';
import searchTextFieldTheme from 'theme/search-text-field-theme';
import globalMessages from 'translations/messages/global-messages';

const SEPARATOR = {
  enter: 'Enter',
  comma: ',',
  semicolon: ';',
  space: ' ',
};

const pasteSeparatorRegex = /[;,\s]/;
const pasteSplitSeparatorRegex = /[;,\s]+/;

function SearchTextField({
  classes,
  className,
  style = {},
  ref = null,
  value = '',
  size,
  multi,
  autoFocus,
  placeHolder,
  disabled = false,
  searchItems = [],
  searchInputProps = {},
  onChange,
  onSearchItemAdd,
  onSearchItemRemove,
}) {
  const intl = useIntl();
  const textInput = useRef(null);

  const handleClear = async () => {
    await onChange('');
    setTimeout(() => {
      if (textInput?.current) textInput.current.focus();
    }, 500);
  };

  const handleKeyDown = async evt => {
    if (!value || !multi || !Object.values(SEPARATOR).includes(evt.key)) return;
    if (!searchItems.find(item => item.value === value) && onSearchItemAdd) {
      await onSearchItemAdd(value);
    }
    await handleClear();
    evt.preventDefault();
  };

  const handlePaste = async evt => {
    if (!multi || !onSearchItemAdd) return;

    const pastedText = evt.clipboardData?.getData('text') || '';
    if (!pasteSeparatorRegex.test(pastedText)) return;

    evt.preventDefault();

    const existingValues = new Set(searchItems.map(item => item.value));
    const incomingValues = pastedText
      .split(pasteSplitSeparatorRegex)
      .map(item => item.trim())
      .filter(Boolean);

    for (let index = 0; index < incomingValues.length; index += 1) {
      const incomingValue = incomingValues[index];
      if (!existingValues.has(incomingValue)) {
        // eslint-disable-next-line no-await-in-loop
        await onSearchItemAdd(incomingValue);
        existingValues.add(incomingValue);
      }
    }

    await handleClear();
  };

  return (
    <div
      className={classNames(
        multi && searchItems.length ? classes.multiSearchTextField : classes.searchTextField,
        className,
      )}
      style={style}
    >
      <TextField
        ref={ref}
        id={`search-${uuid.v4()}`} // avoid autocomplete
        value={value}
        disabled={disabled}
        autoFocus={autoFocus}
        variant="outlined"
        fullWidth
        size={size}
        placeholder={placeHolder || intl.formatMessage(globalMessages.search_dot)}
        InputProps={{
          classes: {
            root: classes.cssInput,
            focused: classes.cssFocused,
            notchedOutline: classes.notchedOutline,
            adornedStart: classes.adornedStart,
            adornedEnd: classes.adornedEnd,
          },
          startAdornment:
            multi && searchItems.length ? (
              <div className="search-text-field-multi-items-wrapper">
                <InputAdornment position="start" disablePointerEvents>
                  <Search className={classes.searchIcon} />
                </InputAdornment>
                {searchItems.map(searchItem => (
                  <SearchTextFieldItem
                    {...searchItem}
                    key={searchItem.value}
                    onRemove={() => onSearchItemRemove(searchItem.value)}
                  />
                ))}
              </div>
            ) : (
              <InputAdornment position="start" disablePointerEvents>
                <Search className={classes.searchIcon} />
              </InputAdornment>
            ),
          endAdornment:
            value && !multi ? (
              <InputAdornment position="end" onClick={handleClear}>
                <Clear className={classes.clearIcon} />
              </InputAdornment>
            ) : null,
          ...searchInputProps,
        }}
        onChange={async evt => {
          await onChange(evt.target.value);
        }}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      />
    </div>
  );
}

SearchTextField.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  ref: PropTypes.object,
  value: PropTypes.string,
  size: PropTypes.string,
  multi: PropTypes.bool,
  autoFocus: PropTypes.bool,
  placeHolder: PropTypes.string,
  disabled: PropTypes.bool,
  searchItems: PropTypes.array,
  searchInputProps: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onSearchItemAdd: PropTypes.func,
  onSearchItemRemove: PropTypes.func,
};

export default withStyles(searchTextFieldTheme)(SearchTextField);
